/**
 * MONSTER TOWN — WHERE YOU ARE STANDING. FISHING/BUGS CREW (src/catching/**).
 *
 * `collection.ts` answers "what is out right now, HERE" and takes `here` as a
 * PLACE TAG — a plain data string it never interprets. This file is the only
 * thing in the game that turns a patch of the town into one of those strings:
 *
 *     pond   the water, and any water the kid painted themselves
 *     tree   under a tree, where the beetles and the cicadas are
 *     garden a flower bed, where the bees are
 *     grass  everywhere else you can stand
 *
 * HOW IT FINDS THEM WITHOUT IMPORTING THE WORLD CREW: it asks. The world answers
 * `world:sceneryRequest` synchronously with every tappable thing it baked, and
 * the ids carry the kind ('tree:x:z', 'bloom:bed0:3'). That is the same
 * conversation the tutorial and the signposts already have with it, so a world
 * that moves its trees moves the beetles with it and no data changes here.
 *
 * The one thing that cannot come over the bus is the POND's SIZE — the landmark
 * reply is deliberately limited to destinations ("the pond is lovely and it is
 * not somewhere to be sent", hotspots.ts). So the pond rectangle is read from
 * `src/world/layout.ts`, which is a table of numbers with no behaviour in it.
 *
 * NOTHING HERE CAN ANSWER "NO". `placeTagAt` is total: every point in the world
 * is somewhere, and the worst case is 'grass', which always has something out
 * in it (see the coverage table in `src/content/creatures.ts`).
 */

import { bus } from '../core/events'
import { world } from '../core/scene'
import type { SceneId, Vec3 } from '../core/types'
import { POND, WALK_IN, WALK_OUT } from '../world/layout'

export type PlaceTag = 'pond' | 'tree' | 'garden' | 'grass'

const TOWN = 'town' as SceneId

/** How far from a trunk still counts as being under the tree. */
const TREE_REACH = 3.0
/** Flower beds are small; a kid aiming at one is aiming at the whole bed. */
const GARDEN_REACH = 2.4
/** The shore ring around the open water, so the bank still reads as 'pond'. */
const POND_SHORE = 2.2

interface Spot {
  x: number
  z: number
}

let trees: Spot[] = []
let blooms: Spot[] = []
let asked = false

/**
 * Ask the world what it baked. Synchronous: the reply lands inside `emit`.
 * An empty answer is NOT cached — the world may simply not have finished
 * building yet, and a cached "there are no trees" would last for ever (the same
 * trap `src/routine/places.ts` documents for landmarks).
 */
function refresh(): void {
  const nextTrees: Spot[] = []
  const nextBlooms: Spot[] = []
  const off = bus.on('world:scenery', (e) => {
    for (const s of e.list) {
      if (s.id.startsWith('tree:')) nextTrees.push({ x: s.at.x, z: s.at.z })
      else if (s.id.startsWith('bloom:')) nextBlooms.push({ x: s.at.x, z: s.at.z })
    }
  })
  bus.emit('world:sceneryRequest', {})
  off()
  if (nextTrees.length === 0 && nextBlooms.length === 0) return
  trees = nextTrees
  blooms = nextBlooms
  asked = true
}

function ensure(): void {
  if (!asked) refresh()
}

/** The town was rebuilt (a slot switch, a cleared plot). Ask again. */
export function forgetScenery(): void {
  asked = false
  trees = []
  blooms = []
}

export function installPlaces(): void {
  bus.on('scene:entered', (e) => {
    if (e.scene === TOWN) ensure()
  })
  bus.on('save:loaded', () => forgetScenery())
}

// ---------------------------------------------------------------------------
// the pond
// ---------------------------------------------------------------------------

/** Open water plus its shore. Used to decide a tap is a cast. */
export function isPondWater(x: number, z: number): boolean {
  return (
    Math.abs(x - POND.cx) <= POND.hx + POND_SHORE && Math.abs(z - POND.cz) <= POND.hz + POND_SHORE
  )
}

/**
 * WHICH BIT OF THE POND. The bank and the water share one `z` so the line is
 * short and the float is in front of the monster rather than across the pond.
 *
 * IT STEERS AROUND THE JETTY. The world crew built a wooden walkway straight
 * across the middle of the water, and the first version cast at the pond's dead
 * centre — the captured frame showed the float sitting on the planks, which
 * reads as a bug even though it is not one. Anything within `JETTY_CLEAR` of
 * the middle is pushed to whichever side of it the monster is already on.
 */
const JETTY_CLEAR = 2.8

function pondZ(from: Vec3): number {
  const clamped = Math.max(POND.cz - POND.hz + 1.4, Math.min(POND.cz + POND.hz - 1.4, from.z))
  const off = clamped - POND.cz
  if (Math.abs(off) >= JETTY_CLEAR) return clamped
  return POND.cz + (off >= 0 ? JETTY_CLEAR + 0.6 : -(JETTY_CLEAR + 0.6))
}

/** Open water in front of the monster — where the float lands. */
export function pondCentre(from: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  return { x: POND.cx + POND.hx - 2.9, y: 0.06, z: pondZ(from) }
}

/**
 * HOW FAR OUT FROM THE OPEN WATER THE BANK IS — and it is HALF A METRE, not
 * two and a half.
 *
 * Round `vr2`: "the monster fishes from the middle of the road... it stands on
 * the pedestrian crossing several metres from the water." Both halves were one
 * number. This used to mirror the world crew's landmark `standAt`
 * (`POND.cx + POND.hx + 2.6` = x −19.0) — and the pond was pushed WEST out of
 * the pavement in a later world-crew pass so that its shore stops flush with
 * the outer pavement at x −20. So −19.0 stopped being "the bank" and became the
 * ring road: the kerb is at 18.8, the crossing runs across it, and the cast
 * went 4.7 m over a road, a verge and a shore to reach the float.
 *
 * A landmark `standAt` is "walk over to the pond and look at it", which is a
 * different question from "stand where you can fish", so this crew answers its
 * own. The open water's east edge is `POND.cx + POND.hx`; half a metre outside
 * it is the sand-and-dirt shore ring the world crew paints (insets 1.0 and
 * 1.75), which is grass-side of the pavement everywhere and is 1.3 m clear of
 * the world's own pond blocker (`x1 = cx + hx − 1.2`) plus the nav mask's
 * 0.45 m margin. `walkableNear` still has the last word, so a kid who fences
 * their shore stands next to the fence rather than inside it, and nothing here
 * can ever refuse (LAW #2).
 */
const BANK_OUT = 0.5

export function pondStand(from: Vec3): Vec3 {
  return walkableNear({ x: POND.cx + POND.hx + BANK_OUT, y: 0, z: pondZ(from) })
}

// ---------------------------------------------------------------------------
// the tag under a point
// ---------------------------------------------------------------------------

function nearest(list: readonly Spot[], x: number, z: number): number {
  let best = Infinity
  for (const s of list) {
    const d = Math.hypot(s.x - x, s.z - z)
    if (d < best) best = d
  }
  return best
}

/**
 * IS THIS TARMAC?
 *
 * Round `vr1`: "the bug is tagged place:'grass' and is standing on the paved
 * crossing, several tiles from the nearest grass." Both halves came from one
 * hole — `spawnSpot`'s open-ground branch asked the grid "can a monster stand
 * here?", and a road is somewhere a monster can stand. So a ladybird spawned on
 * a zebra crossing and then, quite correctly, reported the only tag this crew
 * has for open ground.
 *
 * The ring road and its two pavements are pure LAYOUT DATA — the same table
 * `POND` above is read from, for the same documented reason: numbers with no
 * behaviour in them. The plaza and its four gate paths are drawn by
 * `terrain.ts`'s own local constants and are MIRRORED here, deliberately and
 * visibly: if the world crew ever moves the plaza the worst that happens is a
 * bug back on a paving slab, which is exactly today's behaviour and not a crash.
 * Nothing downstream is allowed to fail on this answer — see `spawnSpot`.
 */
const PLAZA_HALF = 4.5
const GATE_PATH_HALF = 1.15

export function isPaved(x: number, z: number): boolean {
  const ring = Math.max(Math.abs(x), Math.abs(z))
  if (ring >= WALK_IN && ring <= WALK_OUT) return true
  if (Math.abs(x) <= PLAZA_HALF && Math.abs(z) <= PLAZA_HALF) return true
  if (Math.abs(x) <= GATE_PATH_HALF && Math.abs(z) <= WALK_IN) return true
  if (Math.abs(z) <= GATE_PATH_HALF && Math.abs(x) <= WALK_IN) return true
  return false
}

/**
 * A BUG DOES NOT STAND STILL, SO NEITHER DOES THE QUESTION.
 *
 * `bugs.ts` puts every creature on a gentle leash — it pootles round its own
 * patch on a circle of a little over a metre — so "the spawn point is on grass"
 * is not the same claim as "a kid will see it on grass". Fixing only the spawn
 * point moved the round-`vr1` defect from two bugs in twenty to two bugs in
 * twenty: same crossing, one wander later.
 *
 * So the spot has to be clear of paving by the WHOLE leash. The pad mirrors the
 * larger of the two leash radii in `bugs.ts` (a grass bug's 1.15) with a little
 * over; the two files are deliberately not coupled, because the worst case if
 * they drift is a bug near a kerb, which is where this started and is not a
 * crash.
 */
const WANDER_PAD = 1.35

function offTarmac(x: number, z: number, pad = WANDER_PAD): boolean {
  if (isPaved(x, z)) return false
  return (
    !isPaved(x + pad, z) && !isPaved(x - pad, z) && !isPaved(x, z + pad) && !isPaved(x, z - pad)
  )
}

/** TOTAL. Every point in the world is somewhere. */
export function placeTagAt(x: number, z: number): PlaceTag {
  ensure()
  if (isPondWater(x, z)) return 'pond'
  if (nearest(trees, x, z) <= TREE_REACH) return 'tree'
  if (nearest(blooms, x, z) <= GARDEN_REACH) return 'garden'
  return 'grass'
}

/** Every trunk the world baked, for the bug spawner. May be empty. */
export function treeSpots(): readonly Spot[] {
  ensure()
  return trees
}

export function bloomSpots(): readonly Spot[] {
  ensure()
  return blooms
}

// ---------------------------------------------------------------------------
// standing room
// ---------------------------------------------------------------------------

/**
 * The nearest cell to `p` a monster can actually stand on, searched outwards.
 * Same shape as the routine crew's `walkableNear`, and for the same reason: the
 * kid can build anywhere, so no spot in this game may be a fixed coordinate.
 */
export function walkableNear(p: Vec3, radius = 7): Vec3 {
  const node = world.get(TOWN)
  if (node === undefined) return { x: p.x, y: 0, z: p.z }
  const grid = node.grid
  const inBounds = (gx: number, gz: number): boolean =>
    gx >= 0 && gz >= 0 && gx < grid.spec.width && gz < grid.spec.depth
  const c = grid.worldToCell(p.x, p.z, 0)
  if (inBounds(c.gx, c.gz) && grid.isWalkable(c.gx, c.gz)) return { x: p.x, y: 0, z: p.z }
  for (let r = 1; r <= radius; r++) {
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue
        const gx = c.gx + dx
        const gz = c.gz + dz
        if (!inBounds(gx, gz)) continue
        if (!grid.isWalkable(gx, gz)) continue
        return grid.cellToWorld({ gx, gy: 0, gz })
      }
    }
  }
  // Everything within seven tiles is built on. Standing where you are is always
  // allowed — nobody in this game is ever stuck.
  return { x: p.x, y: 0, z: p.z }
}

/** Is this point somewhere a monster could stand? */
export function isStandable(p: Vec3): boolean {
  const node = world.get(TOWN)
  if (node === undefined) return false
  const grid = node.grid
  const c = grid.worldToCell(p.x, p.z, 0)
  if (c.gx < 0 || c.gz < 0 || c.gx >= grid.spec.width || c.gz >= grid.spec.depth) return false
  return grid.isWalkable(c.gx, c.gz)
}

/**
 * Somewhere near the player to put a bug. Prefers the tag the caller asked for
 * (a tree bug wants a tree), and always returns a point a monster can reach —
 * a bug hovering inside a wall is a bug a kid cannot catch, which under LAW #1
 * is the same thing as a prop that does nothing.
 */
export function spawnSpot(
  from: Vec3,
  want: PlaceTag,
  rng: () => number,
): { at: Vec3; place: PlaceTag } | null {
  ensure()
  /**
   * WALK ROUND THE TRUNK (or the flower bed) LOOKING FOR SOMEWHERE THAT IS NOT
   * TARMAC, and give up rather than settle for the kerb.
   *
   * The open-ground branch below learned this in round `vr1` and these two did
   * not, which is a hole of exactly the same shape: the town's ring road and its
   * two pavements run past a good many of the world crew's trees, so "two metres
   * from this trunk" lands on paving often enough to be visible, and a beetle on
   * a zebra crossing is a content bug a three-year-old can see.
   *
   * RETURNING `null` IS NOT REFUSING A CREATURE. It falls through to the open
   * ground pass, which finds real grass and returns 'grass' — and `trySpawn`
   * re-rolls the species from the place it actually got, so what turns up is a
   * grass bug on grass rather than a tree bug on the kerb. LAW #2 is kept by the
   * pass below, which cannot come back empty on a town a monster can stand in.
   */
  const roundAbout = (
    cx: number,
    cz: number,
    radius: number,
    place: PlaceTag,
  ): { at: Vec3; place: PlaceTag } | null => {
    for (let i = 0; i < 10; i++) {
      const ang = rng() * Math.PI * 2
      const r = radius * (0.7 + rng() * 0.9)
      const at = walkableNear({ x: cx + Math.cos(ang) * r, y: 0, z: cz + Math.sin(ang) * r })
      if (offTarmac(at.x, at.z) && !isPondWater(at.x, at.z)) return { at, place }
    }
    return null
  }

  const nearestFirst = (list: readonly Spot[]): Spot[] =>
    [...list].sort(
      (a, b) => Math.hypot(a.x - from.x, a.z - from.z) - Math.hypot(b.x - from.x, b.z - from.z),
    )

  if (want === 'tree' && trees.length > 0) {
    const sorted = nearestFirst(trees)
    const pick = sorted[Math.floor(rng() * Math.min(5, sorted.length))] ?? sorted[0]
    const spot = pick === undefined ? null : roundAbout(pick.x, pick.z, 2, 'tree')
    if (spot !== null) return spot
  }
  if (want === 'garden' && blooms.length > 0) {
    const sorted = nearestFirst(blooms)
    const pick = sorted[Math.floor(rng() * Math.min(6, sorted.length))] ?? sorted[0]
    const spot = pick === undefined ? null : roundAbout(pick.x, pick.z, 1.6, 'garden')
    if (spot !== null) return spot
  }
  /**
   * Open ground: ring the player at a comfortable "I can see it, I'll go and
   * get it" distance and take the first spot that is really standable — and,
   * on the first pass, one that is really GRASS with a leash's worth of grass
   * all round it (`offTarmac`). A bug tagged 'grass' standing on a zebra
   * crossing is a content bug a three-year-old can see.
   *
   * The second pass drops the tarmac test on purpose. A kid who has paved their
   * whole plot must still get creatures: LAW #2 says nothing is ever unfindable,
   * and a bug on a paving slab is a smaller failure than no bug at all.
   */
  for (const avoidTarmac of [true, false]) {
    for (let i = 0; i < 14; i++) {
      const ang = rng() * Math.PI * 2
      const r = 5 + rng() * 8
      const p: Vec3 = { x: from.x + Math.cos(ang) * r, y: 0, z: from.z + Math.sin(ang) * r }
      if (isPondWater(p.x, p.z)) continue
      if (avoidTarmac && !offTarmac(p.x, p.z)) continue
      if (!isStandable(p)) continue
      return { at: { x: p.x, y: 0, z: p.z }, place: placeTagAt(p.x, p.z) }
    }
  }
  return null
}
